import * as React from 'react';
import { connect } from 'react-redux';
import AppHistory from '__common/modules/history';
import { SAVE_LITE_BOM_REQUEST } from 'actions';
import { SELECT_ACCESSORY, UNSELECT_ACCESSORY } from 'bom/bomActions';
import { SET_UBUILDER_LITE, CLEAR_UBUILDER_LITE } from 'app/appActions';
import { FETCH_LITE_BOM_REQUEST, UPDATE_LITE_PROJECT_REQUEST } from 'v3/dashboard/liteProjectActions';
import { OPEN_BOM_REPORT } from 'engineering/components/engineeringProjectDocuments/engineeringProjectDocumentsActions';
import { saveAs } from 'file-saver';
import { getBomCSVName } from 'bom/utils/bomCSVNames';
import { SET_PROJECT_OPTION } from 'projectDesign/components/projectConfiguration/projectConfigurationActions';
import { getProductName, isCommercialProduct, isRM10Evolution, isRmGridflex10D, isRmGridflex5Rwdi, isSolarMount, isSMAscenderFlush, products } from '__common/constants/products';
import { AttachmentType } from 'projectDesign/components/projectConfiguration/fields/attachmentType';
import { s5AttachmentPreferenceDict, isGeneric, S5_DEFAULT_ULTIMATE_LOAD_UPLIFT, S5_DEFAULT_ULTIMATE_LOAD_SHEAR } from 'projectDesign/components/projectConfiguration/utils/s5AttachmentsFilters';
import {
  renderConditionalAttachmentFields as renderSharedConditionalAttachmentFields,
  renderLiteAttachmentSelect,
} from 'v3/bomPage/liteAttachmentFields';
import RailTypeSelector, { getRailTypeOptions } from 'v3/bomPage/RailTypeSelector';
import reduxStore from '__common/store';
import nprogress from 'nprogress';
import {
  BomPart,
  BomEditState,
  initialBomEditState,
  ATTACHMENT_TYPES,
  ATTACHMENT_TYPE_GROUPS,
  attachmentLabelToInt,
  attachmentIntToLabel,
  FINISHES,
  FINISH_COLOR_CODES,
  availableFinishes,
  finishOf,
  resolveVariant,
  partQtyDisplay,
  partQtyNumber,
  isAccessoryAdded,
  accessoryQtyNumber,
  accessoryQtyDisplay,
  isValidQtyInput,
  buildBomSavePayload,
  seedRowFinishFromSelections,
  getAugmentedPartsList,
  seedAccessoryQtysFromSelections,
  seedAccessoryQtysFromPartsList,
  cacheBomEditState,
  readBomEditCache,
  sortLiteBom,
  isSelectedUAnchorPart,
} from 'v3/bomPage/liteBomEdits';
import { hitApi } from '__common/utils/api';
import { RoofType } from 'projectDesign/components/projectConfiguration/utils/constants';

type Props = {
  match: { params: { productId: string; projectId: string } };
  projectName: string;
  partsList: BomPart[];
  accessoriesList: BomPart[];
  totalModules: number;
  totalWatts: number;
  unitsType: 'customary' | 'metric';
  domesticContent: boolean;
  // True when there's a pending lite autosave/BOM PUT or chained GET BOM in
  // flight. Same formula as BomPanel — drives the "Updating BOM…" overlay
  // that appears while a server-side regen (e.g. after a domestic_content
  // toggle) is being processed. Also gates the deferred CSV download in
  // componentDidUpdate.
  isUpdating: boolean;
  // BOM fetch lifecycle flags. Mirrors BomPanel — used to gate the initial
  // loading screen.
  //   bomReady: bom.isReady, true after the first successful fetch lands.
  //   bomError: bom.error, populated on FETCH_BOM_PART_LIST_ERROR.
  bomReady: boolean;
  bomError: any;
  extraParameters?: any;
  productId?: number;
  attachmentTypeInt?: number;
  roofType?: number;
  s5OrSci?: string;
  tileReplacementOrSolarhooks?: number;
  railsProductId?: number;
  railClampOption?: number;
  materialThickness?: number;
  s5RoofManufacturer?: string;
  s5RoofPanel?: string;
  s5RoofMaterial?: string;
  s5Clamp?: string;
  s5UltimateLoadUplift?: number;
  s5UltimateLoadShear?: number;
  roofSubstrate?: number;
  standoffHeight?: number;
  manufacturer?: string;
  colour?: string;
  allowMechanicalAttachments?: number;
  attachmentOptimizationCriteria?: number;
  upliftAllowable?: number;
  shearAllowable?: number;
  dispatch: Function;
  moduleLength?: number;
  snowLoad?: number;
  railFinish?: string;
  uanchorPartNumber?: string;
};

const fmtMoney = (n: number) =>
  '$' + (Number.isFinite(n) ? n : 0).toFixed(2);

const fmtToday = () => {
  const d = new Date();
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

type State = BomEditState & {
  emailModalOpen: boolean;
  emailModalView: 'default' | 'success';
  emailRecipient: string;
  emailMessage: string;
  emailSending: boolean;
  toastMsg: string | null;
  reportDownloading: boolean;
  reportLoaderMsg: string;
  downloadMenuOpen: boolean;
  pendingPdfDownload: boolean;
  pendingPdfAfterFlush: boolean;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class CompleteBomPage extends React.Component<Props, State> {
  state: State = {
    ...initialBomEditState(),
    emailModalOpen: false,
    emailModalView: 'default',
    emailRecipient: '',
    emailMessage: '',
    emailSending: false,
    toastMsg: null,
    reportDownloading: false,
    reportLoaderMsg: '',
    downloadMenuOpen: false,
    pendingPdfDownload: false,
    pendingPdfAfterFlush: false,
  };

  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private toastTimer: number | null = null;
  private storeUnsubscribe: (() => void) | null = null;
  private reportTimeoutTimer: number | null = null;
  private nprogressPollTimer: number | null = null;
  private sawNprogressActive = false;
  private nprogressIdleTicks = 0;

  // URL slug → canonical product name string consumed by the lite BOM API.
  // The workspace does this conversion in UBuilderLiteContainer before
  // dispatching FETCH_LITE_BOM_REQUEST; passing the raw URL slug instead
  // either returns empty or 4xx because the backend keys on canonical names
  // (e.g. URL "nxt_horizon" maps to canonical "nxt_umount").
  private canonicalProductName(): string {
    const slug = this.props.match.params.productId;
    if (!slug) return '';
    const numeric = products[slug];
    return numeric ? getProductName(numeric) : '';
  }

  componentDidMount() {
    const { dispatch, match: { params: { projectId } }, bomReady } = this.props;
    const productName = this.canonicalProductName();

    const currentBomProjectId = reduxStore.getState().bom.projectId;
    dispatch(SET_UBUILDER_LITE(true));
    // Fetch BOM on mount if it's not already ready for this project, or if
    // an update is already in-flight (in which case we skip our own dispatch
    // and let the in-flight one resolve it).
    if (projectId && productName) {
      dispatch(FETCH_LITE_BOM_REQUEST({ projectId, productId: productName }));
    }

    const cached = readBomEditCache(projectId);
    if (cached) {
      this.setState({
        rowFinish: cached.rowFinish,
        partQty: cached.partQty,
        addedAccessories: cached.addedAccessories,
        accessoryQty: cached.accessoryQty,
        discountPct: cached.discountPct,
      });
    }

    this.seedRowFinishFromProps();

    const { s5OrSci, s5RoofManufacturer, s5RoofPanel, s5RoofMaterial, s5Clamp, s5UltimateLoadUplift, s5UltimateLoadShear } = this.props;
    if (s5OrSci === s5AttachmentPreferenceDict.s_5) {
      const needsUpdate =
        s5RoofManufacturer !== isGeneric ||
        s5RoofPanel !== isGeneric ||
        s5RoofMaterial !== isGeneric ||
        !s5Clamp ||
        !s5UltimateLoadUplift ||
        !s5UltimateLoadShear;
      if (needsUpdate && dispatch) {
        const nextClamp = s5Clamp || 'S-5-N Mini';
        const nextUplift = s5UltimateLoadUplift || S5_DEFAULT_ULTIMATE_LOAD_UPLIFT;
        const nextShear = s5UltimateLoadShear || S5_DEFAULT_ULTIMATE_LOAD_SHEAR;
        dispatch(SET_PROJECT_OPTION('s5_roof_manufacturer', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_roof_panel', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_roof_material', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_clamp', nextClamp));
        dispatch(SET_PROJECT_OPTION('s5_ultimate_load_uplift', nextUplift));
        dispatch(SET_PROJECT_OPTION('s5_ultimate_load_shear', nextShear));
        this.persistFieldChange({
          s5_roof_manufacturer: isGeneric,
          s5_roof_panel: isGeneric,
          s5_roof_material: isGeneric,
          s5_clamp: nextClamp,
          s5_ultimate_load_uplift: nextUplift,
          s5_ultimate_load_shear: nextShear,
        });
      }
    }

    this.storeUnsubscribe = reduxStore.subscribe(() => {
      if (!this.state.reportDownloading) return;
      const s: any = reduxStore.getState();
      const msg: string = (s && s.app && s.app.loaderMsg) || '';
      const lower = msg.toLowerCase();
      if (msg && msg !== this.state.reportLoaderMsg) {
        this.setState({ reportLoaderMsg: msg });
      }
      if (lower.includes('failure') || lower.includes('error')) {
        this.finishReportDownload();
        this.showToast(`Report download failed: ${msg}`);
      }
    });
  }

  private isNprogressActive = (): boolean => {
    const np: any = nprogress as any;
    const s = np && (typeof np.status !== 'undefined' ? np.status : (np.default && np.default.status));
    return s !== null && s !== undefined;
  }

  private startNprogressPoll = () => {
    this.stopNprogressPoll();
    this.sawNprogressActive = false;
    this.nprogressIdleTicks = 0;
    this.nprogressPollTimer = window.setInterval(() => {
      if (!this.state.reportDownloading) {
        this.stopNprogressPoll();
        return;
      }
      const active = this.isNprogressActive();
      if (active) {
        this.sawNprogressActive = true;
        this.nprogressIdleTicks = 0;
      } else if (this.sawNprogressActive) {
        this.nprogressIdleTicks += 1;
        if (this.nprogressIdleTicks >= 5) {
          this.finishReportDownload();
        }
      }
    }, 200);
  }

  private stopNprogressPoll = () => {
    if (this.nprogressPollTimer !== null) {
      window.clearInterval(this.nprogressPollTimer);
      this.nprogressPollTimer = null;
    }
  }

  componentDidUpdate(prevProps: Props) {
    // Rail-type swaps from the rail-type selector rewrite the URL via
    // history.replace (same :, same component instance, new params).
    // Refetch the BOM under the new product id so the user sees a freshly
    // computed parts list instead of stale parts for the previous rail family.
    const prevSlug = prevProps.match && prevProps.match.params && prevProps.match.params.productId;
    const nextSlug = this.props.match && this.props.match.params && this.props.match.params.productId;
    if (prevSlug !== nextSlug && nextSlug) {
      const nextProductName = this.canonicalProductName();
      const projectId = this.props.match.params.projectId;
      if (projectId && nextProductName) {
        this.props.dispatch(FETCH_LITE_BOM_REQUEST({ projectId, productId: nextProductName }));
      }
    }
    if (prevProps.partsList !== this.props.partsList || prevProps.extraParameters !== this.props.extraParameters || prevProps.accessoriesList !== this.props.accessoriesList) {
      this.seedRowFinishFromProps();
    }

    const { s5OrSci, s5RoofManufacturer, s5RoofPanel, s5RoofMaterial, s5Clamp, s5UltimateLoadUplift, s5UltimateLoadShear, dispatch } = this.props;
    if (s5OrSci === s5AttachmentPreferenceDict.s_5) {
      const needsUpdate =
        s5RoofManufacturer !== isGeneric ||
        s5RoofPanel !== isGeneric ||
        s5RoofMaterial !== isGeneric ||
        !s5Clamp ||
        !s5UltimateLoadUplift ||
        !s5UltimateLoadShear;
      if (needsUpdate && dispatch) {
        const nextClamp = s5Clamp || 'S-5-N Mini';
        const nextUplift = s5UltimateLoadUplift || S5_DEFAULT_ULTIMATE_LOAD_UPLIFT;
        const nextShear = s5UltimateLoadShear || S5_DEFAULT_ULTIMATE_LOAD_SHEAR;
        dispatch(SET_PROJECT_OPTION('s5_roof_manufacturer', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_roof_panel', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_roof_material', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_clamp', nextClamp));
        dispatch(SET_PROJECT_OPTION('s5_ultimate_load_uplift', nextUplift));
        dispatch(SET_PROJECT_OPTION('s5_ultimate_load_shear', nextShear));
        this.persistFieldChange({
          s5_roof_manufacturer: isGeneric,
          s5_roof_panel: isGeneric,
          s5_roof_material: isGeneric,
          s5_clamp: nextClamp,
          s5_ultimate_load_uplift: nextUplift,
          s5_ultimate_load_shear: nextShear,
        });
      }
    }

    const updatingJustEnded = prevProps.isUpdating && !this.props.isUpdating;

    if (this.state.pendingPdfDownload && this.props.bomReady && updatingJustEnded) {
      const hasParts = !!(this.props.partsList && this.props.partsList.length > 0);
      if (hasParts) {
        this.flushBomSave();
        this.setState({ pendingPdfDownload: false, pendingPdfAfterFlush: true });
      } else {
        this.setState({ pendingPdfDownload: false });
        this.dispatchPdfDownload();
      }
    }

    if (this.state.pendingPdfAfterFlush && updatingJustEnded) {
      this.setState({ pendingPdfAfterFlush: false });
      this.dispatchPdfDownload();
    }

    if (!this.state.pendingCsvDownload) return;
    if (!this.props.bomReady) return;
    if (updatingJustEnded) {
      this.setState({ pendingCsvDownload: false });
      this.dispatchCsvDownload();
    }
  }

  // Merge persisted color_finish into local state without clobbering any
  // edits the user has already made in this session (state.rowFinish wins).
  private seedRowFinishFromProps = () => {
    const { partsList, accessoriesList, extraParameters } = this.props;
    if (!partsList || partsList.length === 0) return;
    const seeded = seedRowFinishFromSelections(partsList, extraParameters);
    const seededAccessoryQty = seedAccessoryQtysFromSelections(accessoriesList || [], extraParameters);
    // Accessories returned in BOTH parts_list and accessories_list take their
    // qty from parts_list (the authoritative build quantity).
    const seededAccessoryQtyFromParts = seedAccessoryQtysFromPartsList(accessoriesList || [], partsList);
    const seededDiscountPct = (extraParameters && extraParameters.discount_pct !== undefined)
      ? String(extraParameters.discount_pct)
      : undefined;

    this.setState((prev) => {
      let changed = false;
      const nextRowFinish = { ...prev.rowFinish };
      let normalizedFinish = undefined;
      if (this.props.railFinish) {
        for (const finish of Object.keys(FINISH_COLOR_CODES)) {
          if ((FINISH_COLOR_CODES[finish] || []).includes(this.props.railFinish.toUpperCase())) {
            normalizedFinish = finish;
            break;
          }
        }
        if (!normalizedFinish) {
          normalizedFinish = FINISHES.find(f => f.toUpperCase() === this.props.railFinish.toUpperCase());
        }
      }

      Object.keys(seeded).forEach((k) => {
        if (nextRowFinish[k] === undefined) {
          nextRowFinish[k] = seeded[k];
          changed = true;
        }
      });

      // If we still have parts without a finish selection, use the project's preferred finish
      (partsList || []).forEach(p => {
        if (nextRowFinish[p.number] === undefined && normalizedFinish) {
          nextRowFinish[p.number] = normalizedFinish;
          changed = true;
        }
      });
      const nextAccessoryQty = { ...prev.accessoryQty };
      // parts_list qty wins for accessories present in both lists, but only
      // while the user hasn't toggled the accessory this session — a
      // remove + re-add must fall back to the accessories_list suggested qty
      // rather than re-pulling the parts_list value.
      Object.keys(seededAccessoryQtyFromParts).forEach((k) => {
        if (prev.addedAccessories[k] === undefined) {
          const newValue = seededAccessoryQtyFromParts[k];
          if (nextAccessoryQty[k] === undefined || (nextAccessoryQty[k] !== newValue && !this.props.isUpdating)) {
            nextAccessoryQty[k] = newValue;
            changed = true;
          }
        }
      });
      Object.keys(seededAccessoryQty).forEach((k) => {
        if (nextAccessoryQty[k] === undefined) {
          nextAccessoryQty[k] = seededAccessoryQty[k];
          changed = true;
        }
      });
      let nextDiscountPct = prev.discountPct;
      if (seededDiscountPct !== undefined && nextDiscountPct === undefined) {
        nextDiscountPct = seededDiscountPct;
        changed = true;
      }

      const nextAddedAccessories = { ...prev.addedAccessories };
      (accessoriesList || []).forEach(a => {
        if (a && nextAddedAccessories[a.number] === undefined && a.selected) {
          nextAddedAccessories[a.number] = true;
          changed = true;
        }
      });

      return changed
        ? { rowFinish: nextRowFinish, accessoryQty: nextAccessoryQty, discountPct: nextDiscountPct, addedAccessories: nextAddedAccessories }
        : null;
    });
  }

  componentWillUnmount() {
    cacheBomEditState(this.props.match && this.props.match.params && this.props.match.params.projectId, this.state);
    if (this.toastTimer !== null) {
      window.clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }
    if (this.storeUnsubscribe) {
      this.storeUnsubscribe();
      this.storeUnsubscribe = null;
    }
    if (this.reportTimeoutTimer !== null) {
      window.clearTimeout(this.reportTimeoutTimer);
      this.reportTimeoutTimer = null;
    }
    this.stopNprogressPoll();
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
      this.flushBomSave();
    }
    this.props.dispatch(CLEAR_UBUILDER_LITE());
  }

  private setRowFinish = (partNumber: string, finish: string) => {
    this.setState(
      prev => ({ rowFinish: { ...prev.rowFinish, [partNumber]: finish } }),
      this.scheduleBomSave,
    );
  };

  private setPartQty = (number: string, raw: string) => {
    if (!isValidQtyInput(raw)) return;
    this.setState(
      prev => ({ partQty: { ...prev.partQty, [number]: raw } }),
      this.scheduleBomSave,
    );
  };

  private setAccessoryQty = (number: string, raw: string) => {
    if (!isValidQtyInput(raw)) return;
    this.setState(
      prev => ({ accessoryQty: { ...prev.accessoryQty, [number]: raw } }),
      this.scheduleBomSave,
    );
  };

  private toggleAccessory = (number: string) => {
    // Resolve current added-state via the same predicate the render path
    // uses (local override first, then Redux a.selected). Reading
    // prev.addedAccessories alone misses items added on the other page.
    const accessory = (this.props.accessoriesList || []).find(a => a && a.number === number);
    const currentlyAdded = isAccessoryAdded(
      accessory || ({ number } as BomPart),
      this.state,
    );
    const nextAdded = !currentlyAdded;
    // Mirror the toggle into Redux so bom.accessoriesList[X].selected
    // persists across page navigation. The lite endpoint writes
    // user_builds but the GET response does not reflect user_builds
    // membership on accessories_list[].selected, so Redux is the
    // client-owned source of truth for the cross-page sync.
    if (this.props.dispatch) {
      this.props.dispatch(nextAdded ? SELECT_ACCESSORY(number) : UNSELECT_ACCESSORY(number));
    }
    this.setState(
      prev => {
        const nextAddedMap = { ...prev.addedAccessories, [number]: nextAdded };
        const nextQty = { ...prev.accessoryQty };
        if (!nextAdded) delete nextQty[number];
        return { addedAccessories: nextAddedMap, accessoryQty: nextQty };
      },
      this.scheduleBomSave,
    );
  };

  private scheduleBomSave = () => {
    cacheBomEditState(this.props.match && this.props.match.params && this.props.match.params.projectId, this.state);
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      this.flushBomSave();
    }, 600);
  };

  private flushBomSave = () => {
    const { dispatch, partsList, accessoriesList, extraParameters, match, domesticContent, totalModules } = this.props;
    const projectId = match && match.params && match.params.projectId;
    if (!projectId || !dispatch) return;
    // Backend treats parts_list:[] as "wipe user_builds" (see
    // updateLiteProject epic regen branch). Bail out rather than
    // accidentally destroy server state from any future caller that
    // forgets to gate at the call site.
    if (!(partsList && partsList.length > 0)) return;
    // The URL slug is authoritative on this workspace-less route — pc.productId
    // stays at the store default (solarmount_2), so deriving the canonical name
    // and the Mid Support injection from it would save under the wrong product
    // and drop the mandatory Mid Support part.
    const resolvedProductId = this.resolveProductId();
    const productName = resolvedProductId ? getProductName(resolvedProductId) : this.canonicalProductName();
    if (!productName) return;
    const { moduleLength, snowLoad } = this.props;
    const augmentedPartsList = getAugmentedPartsList(partsList, resolvedProductId, domesticContent, totalModules, moduleLength, snowLoad);
    const body = buildBomSavePayload({
      state: this.state,
      partsList: augmentedPartsList,
      accessoriesList: accessoriesList || [],
      extraParameters,
    });
    // Do NOT set chainFetch:true. See BomPanel.flushBomSave for rationale:
    // a chained FETCH_BOM_PART_LIST_SUCCESS triggers the legacy
    // autoSaveBomWhenUserBuildNotGenerated epic which writes a second time
    // with potentially stale bom.partsList — visible as a price flicker /
    // double-count on accessory toggles. Sync between pages is handled by
    // the mount-time fetch on the destination page.
    dispatch(SAVE_LITE_BOM_REQUEST({
      projectId,
      productId: productName,
      body,
    }));
  };

  private finishReportDownload = () => {
    if (this.reportTimeoutTimer !== null) {
      window.clearTimeout(this.reportTimeoutTimer);
      this.reportTimeoutTimer = null;
    }
    this.stopNprogressPoll();
    this.setState({ reportDownloading: false, reportLoaderMsg: '' });
  }

  goBack = () => {
    const { productId, projectId } = this.props.match.params;
    AppHistory.push(`/project/lite/design/${productId}/${projectId}`);
  }

  goDashboard = () => {
    AppHistory.push('/lite/dashboard');
  }

  showToast = (msg: string, durationMs: number = 3000) => {
    if (this.toastTimer !== null) window.clearTimeout(this.toastTimer);
    this.setState({ toastMsg: msg });
    this.toastTimer = window.setTimeout(() => this.setState({ toastMsg: null }), durationMs);
  }

  toggleDownloadMenu = () => {
    this.setState((prev) => ({ downloadMenuOpen: !prev.downloadMenuOpen }));
  }

  closeDownloadMenu = () => {
    if (this.state.downloadMenuOpen) this.setState({ downloadMenuOpen: false });
  }

  // Numeric productId for this page. The URL slug is authoritative — this
  // route never mounts the workspace, so pc.productId (this.props.productId)
  // stays at the store default (solarmount_2). Derive from the slug first and
  // fall back to props only when the slug can't be resolved.
  private resolveProductId(): number | undefined {
    const slugProductId = products[this.props.match.params.productId];
    return (slugProductId !== undefined && slugProductId !== null)
      ? slugProductId
      : this.props.productId;
  }

  // Like resolveProductId, this route never mounts the workspace, so
  // projectConfiguration.railsProductId (props.railsProductId) is null on first
  // load — UBuilderLiteContainer.componentWillUnmount dispatches
  // CLEAR_PROJECT_CONFIGURATION on the design→BOM navigation, wiping the
  // in-Redux selection the workspace had populated. The other attachment fields
  // fall back to the persisted snapshot the BOM GET carries in
  // extra_parameters.lite_project_env (see envOr above); railsProductId was the
  // one field missing that fallback, so the rail-type dropdown snapped to the
  // first option (e.g. NXT Standard) instead of the saved selection (e.g. NXT
  // HD). For NXT/SM, changing the rail type on the design page updates the URL
  // via AppHistory.replace (RailTypeSelector.onChange), so the URL slug already
  // encodes the selected rail product — the URL-derived productId IS the
  // railsProductId. Mirror resolveProductId by reading from the URL when Redux
  // has been cleared, gated on getRailTypeOptions so non-NXT/SM products get
  // undefined (no rail-type selector renders for them anyway).
  private resolveRailsProductId(): number | undefined {
    if (this.props.railsProductId) {
      const productId = this.resolveProductId();
      const options = getRailTypeOptions(productId, this.props.roofType);
      if (options.some((option) => option.value === this.props.railsProductId)) {
        return this.props.railsProductId;
      }
    }
    const productId = this.resolveProductId();
    const options = getRailTypeOptions(productId, this.props.roofType);
    if (!options.length) return undefined;
    return options.some((option) => option.value === productId) ? productId : options[0].value;
  }

  // Persist a project_configuration field edit made on the full BOM page.
  // Sends a minimal partial update (configOverride) carrying ONLY the changed
  // field(s), so the backend updates just those columns and leaves every other
  // persisted field intact — instead of being overwritten by the store-default
  // snapshot buildLiteUpdatePayload would serialize from this workspace-less
  // route. The resolved productId is passed for the BOM regen/fetch chain only
  // (not written to the project). regenBom/allowEmptyPanels keep that chain
  // working (see updateLiteProject epic).
  private persistFieldChange = (fields: { [key: string]: any }) => {
    const { dispatch } = this.props;
    const projectId = this.props.match && this.props.match.params && this.props.match.params.projectId;
    const productId = this.resolveProductId();
    if (!dispatch || !projectId || productId === undefined || productId === null) return;
    dispatch(UPDATE_LITE_PROJECT_REQUEST({
      projectId,
      regenBom: true,
      allowEmptyPanels: true,
      configOverride: { productId, fields },
    }));
  }

  handleDomesticToggle = () => {
    const { dispatch, domesticContent } = this.props;
    if (!dispatch) return;
    const nextValue = domesticContent ? 0 : 1;
    dispatch(SET_PROJECT_OPTION('domestic_content', nextValue));
    dispatch(SET_PROJECT_OPTION('is_domestic_mlpe_required', domesticContent ? 0 : 1));
    // SET_PROJECT_OPTION only mutates Redux. persistFieldChange sends the
    // partial update that persists just domestic_content and, via regenBom:true
    // → PUT ?regen_bom=true (backend wipes user_builds) → FETCH_LITE_BOM_REQUEST,
    // rebuilds user_builds and refreshes partsList.
    this.persistFieldChange({ domestic_content: nextValue });
  }

  private dispatchPdfDownload = () => {
    const projectId = this.props.match.params.projectId;
    const productId = this.props.match.params.productId;
    if (!projectId || !productId) {
      this.showToast('Missing project — cannot download');
      return;
    }
    const units = this.props.unitsType || 'customary';
    this.setState({ reportDownloading: true, reportLoaderMsg: 'Preparing BOM…' });
    this.startNprogressPoll();
    if (this.reportTimeoutTimer !== null) window.clearTimeout(this.reportTimeoutTimer);
    this.reportTimeoutTimer = window.setTimeout(this.finishReportDownload, 180000);
    this.props.dispatch(OPEN_BOM_REPORT(projectId, productId, units));
  }

  handleDownloadBom = () => {
    this.setState({ downloadMenuOpen: false });
    if (this.state.reportDownloading) return;
    const projectId = this.props.match.params.projectId;
    const productId = this.props.match.params.productId;
    if (!projectId || !productId) {
      this.showToast('Missing project — cannot download');
      return;
    }
    if (this.saveTimer) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    window.open('about:blank', `download_window_${projectId}`);
    const { partsList } = this.props;
    const hasParts = !!(partsList && partsList.length > 0);
    if (this.props.isUpdating) {
      this.setState({ pendingPdfDownload: true });
      return;
    }
    if (hasParts) {
      this.flushBomSave();
      this.setState({ pendingPdfDownload: true });
      return;
    }
    this.dispatchPdfDownload();
  }

  private dispatchCsvDownload = () => {
    const { partsList, accessoriesList, extraParameters } = this.props;
    const projectId = this.props.match.params.projectId;
    const productId = this.props.match.params.productId;
    if (!projectId || !productId) {
      this.showToast('Missing project — cannot download');
      return;
    }

    const headers = [
      'PART NUMBER', 'PART TYPE', 'DESCRIPTION', 'QUANTITY',
      'SUGGESTED QUANTITY', 'UNIT PRICE (USD)', 'TOTAL LIST PRICE (USD)',
    ];

    const accessoryNumbers = new Set(
      (accessoriesList || []).map(a => a && a.number).filter(Boolean)
    );

    const csvRows: string[][] = [];

    (partsList || [])
      .filter(p => p && !accessoryNumbers.has(p.number))
      .forEach(p => {
        const resolved = resolveVariant(p, this.state, extraParameters);
        const qty = partQtyNumber(p, this.state);
        const unitPrice = isSelectedUAnchorPart(resolved.number, this.props.uanchorPartNumber)
          || isSelectedUAnchorPart(p.number, this.props.uanchorPartNumber)
          ? 0 : (Number(resolved.price) || 0);
        csvRows.push([
          resolved.number || p.number,
          p.type || '',
          resolved.description || p.description || '',
          String(qty),
          String(p.suggested_quantity),
          unitPrice.toFixed(2),
          (unitPrice * qty).toFixed(2),
        ]);
      });

    (accessoriesList || [])
      .filter(a => isAccessoryAdded(a, this.state))
      .forEach(a => {
        const qty = accessoryQtyNumber(a, this.state);
        const unitPrice = isSelectedUAnchorPart(a.number, this.props.uanchorPartNumber)
          ? 0 : (Number(a.price) || 0);
        csvRows.push([
          a.number,
          a.type || '',
          a.description || '',
          String(qty),
          String(a.suggested_quantity),
          unitPrice.toFixed(2),
          (unitPrice * qty).toFixed(2),
        ]);
      });

    const csvContent = [headers, ...csvRows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    saveAs(blob, `${productId.toLocaleUpperCase()}_${getBomCSVName(projectId)}.csv`);
  }

  handleDownloadCsv = () => {
    this.setState({ downloadMenuOpen: false });
    this.dispatchCsvDownload();
  }

  openEmailModal = () => {
    this.setState({
      emailModalOpen: true,
      emailModalView: 'default',
      emailRecipient: '',
      emailMessage: '',
    });
  }

  closeEmailModal = () => {
    this.setState({ emailModalOpen: false });
  }

  sendExportEmail = () => {
    const { emailRecipient, emailMessage } = this.state;
    const projectId = this.props.match.params.projectId;
    this.setState({ emailSending: true });
    const req = hitApi('POST', `api/v1/ub_lite/bom/send_bom_email/`, {
      project_id: projectId,
      distributor_email: emailRecipient.trim(),
      email_body: emailMessage,
    });
    fetch(req.url, {
      method: req.method,
      headers: req.headers as any,
      body: JSON.stringify(req.body),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Server error: ${res.status}`);
        this.setState({ emailModalView: 'success', emailSending: false });
      })
      .catch(() => {
        this.setState({ emailSending: false });
        this.showToast('Failed to send email. Please try again.');
      });
  }

  private resolveRow = (
    p: BomPart,
    qtyAccessor: 'part' | 'accessory',
    buildSuggestedQty?: number,
  ): { resolved: BomPart; qty: number; total: number } => {
    const resolved = resolveVariant(p, this.state, this.props.extraParameters);
    const qty = qtyAccessor === 'part'
      ? partQtyNumber(p, this.state)
      : accessoryQtyNumber(p, this.state, buildSuggestedQty);
    const hidePrice = isSelectedUAnchorPart(resolved.number, this.props.uanchorPartNumber)
      || isSelectedUAnchorPart(p.number, this.props.uanchorPartNumber);
    const unitPrice = hidePrice ? 0 : Number(resolved.price);
    return { resolved, qty, total: unitPrice * qty };
  };

  private renderEditableRow = (
    p: BomPart,
    badgeKind: 'sys' | 'acc' | 'att',
    badgeLabel: string,
    qtyAccessor: 'part' | 'accessory',
    onRemove?: () => void,
    partsByNumber?: Map<string, BomPart>,
  ) => {
    const isToggledThisSession = this.state.addedAccessories[p.number] !== undefined;
    const buildPart = (partsByNumber && !isToggledThisSession) ? partsByNumber.get(p.number) : undefined;
    const buildSuggestedQty = buildPart ? buildPart.suggested_quantity : undefined;
    const { resolved, qty, total } = this.resolveRow(p, qtyAccessor, buildSuggestedQty);
    const suggested = buildSuggestedQty !== undefined ? buildSuggestedQty : (p.suggested_quantity || 0);
    const finishOpts = availableFinishes(p);
    const hasFinish = finishOpts.length > 1;
    const setQty = qtyAccessor === 'part' ? this.setPartQty : this.setAccessoryQty;
    const qtyDisplay = qtyAccessor === 'part'
      ? partQtyDisplay(p, this.state)
      : accessoryQtyDisplay(p, this.state, buildSuggestedQty);
    // Accessories carry the part-type name in `name` and the human-readable
    // text in `description`; show the description on top for them.
    const titleText = badgeKind === 'acc'
      ? (p.description || p.name)
      : (resolved.name || resolved.description || p.name || p.description);
    return (
      <tr key={`${badgeKind}-${p.number}`}>
        <td>
          <div style={{ fontWeight: 600 }}>{titleText}</div>
          <div className="bom-pv-partno">{resolved.number}</div>
          {isSelectedUAnchorPart(resolved.number, this.props.uanchorPartNumber)
            || isSelectedUAnchorPart(p.number, this.props.uanchorPartNumber) ? (
            <div className="v3-bom-pricing-note">Contact Unirac for Pricing</div>
          ) : null}
          {hasFinish && (
            <select
              className="v3-bom-finish-sel v3-bom-finish-sel--short"
              value={finishOf(p, this.state, this.props.extraParameters)}
              onChange={(e: any) => this.setRowFinish(p.number, e.target.value)}
              style={{ marginTop: 4 }}>
              {finishOpts.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          )}
          {p && p.type === 'Rail' && p.category === 'Base' && (
            <div
              className="v3-bom-rail-type-wrap"
              style={{ marginTop: 6 }}
              onClick={(e: any) => e.stopPropagation()}>
              <RailTypeSelector
                productId={this.resolveProductId()}
                railsProductId={this.resolveRailsProductId()}
                roofType={this.props.roofType}
                projectId={this.props.match.params.projectId}
                dispatch={this.props.dispatch}
              />
            </div>
          )}
        </td>
        <td><span className={`bom-pv-badge ${badgeKind}`}>{badgeLabel}</span></td>
        <td className="num">{suggested}</td>
        <td className="num">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="bom-pv-qty-input"
            value={qtyDisplay}
            onChange={(e: any) => setQty(p.number, e.target.value)}
          />
        </td>
        <td className="num">{isSelectedUAnchorPart(resolved.number, this.props.uanchorPartNumber)
          || isSelectedUAnchorPart(p.number, this.props.uanchorPartNumber)
          ? fmtMoney(0) : fmtMoney(Number(resolved.price))}</td>
        <td className="strong">
          {fmtMoney(total)}
          {onRemove && (
            <button
              type="button"
              className="bom-pv-row-remove"
              onClick={onRemove}
              title="Remove"
              style={{ marginLeft: 8 }}>×</button>
          )}
        </td>
      </tr>
    );
  };

  private isCommercialRM = (): boolean => {
    // The URL slug is the authoritative product for this BOM page. On a full
    // page-reload / deep-link, componentDidMount only fetches the BOM (not the
    // project), so pc.productId stays at the store default (solarmount_2) — not
    // undefined — which would wrongly resurface the RM attachment-type dropdown
    // here. Derive from the slug first (same source as canonicalProductName)
    // and fall back to props only when the slug can't be resolved.
    const slugProductId = products[this.props.match.params.productId];
    const productId = (slugProductId !== undefined && slugProductId !== null)
      ? slugProductId
      : this.props.productId;
    return productId !== undefined && productId !== null
      && isCommercialProduct(productId);
  };

  // Delegates to the shared renderLiteAttachmentSelect helper (also used by
  // BomPanel). The onPersist callback maps the grouped-picker patch to
  // CompleteBomPage's persistence flow — persistFieldChange fires a single
  // configOverride PUT that regenerates the BOM. Falls back to the static
  // ATTACHMENT_TYPE_GROUPS list when the shared helper returns null (non
  // NXT/SM products).
  private renderAttachmentDropdown = () => {
    const { dispatch, attachmentTypeInt, roofType, s5OrSci, tileReplacementOrSolarhooks } = this.props;
    const productId = this.resolveProductId();
    const railsProductId = this.resolveRailsProductId();
    const isSmAscenderShingle = !!productId
      && isSolarMount(productId)
      && isSMAscenderFlush(railsProductId)
      && roofType === RoofType.SHINGLE;
    if (isSmAscenderShingle) {
      const options = [
        { value: AttachmentType.L_FOOT_ONLY, name: 'L Foot' },
        { value: AttachmentType.FLASHKIT_PRO, name: 'Flashkit Pro' },
        { value: AttachmentType.FLASHKIT_PRO_SB, name: 'Flashkit Pro SB' },
      ];
      const currentStr = attachmentTypeInt !== undefined && attachmentTypeInt !== null ? String(attachmentTypeInt) : '';
      return (
        <select
          className="bom-pv-att-select"
          value={currentStr}
          onChange={(e: any) => {
            const next = Number(e.target.value);
            if (dispatch) {
              dispatch(SET_PROJECT_OPTION('attachment_type', next));
              this.persistFieldChange({ attachment_type: next });
            }
          }}>
          {!options.some(opt => String(opt.value) === currentStr) && currentStr && (
            <option value={currentStr}>{`Current (legacy: ${currentStr})`}</option>
          )}
          {options.map(opt => (
            <option key={String(opt.value)} value={String(opt.value)}>{opt.name}</option>
          ))}
        </select>
      );
    }
    const shared = renderLiteAttachmentSelect({
      productId,
      roofType,
      railsProductId,
      attachmentTypeInt,
      s5OrSci,
      tileReplacementOrSolarhooks,
      isUbuilderLite: true,
      dispatch,
      selectClassName: 'bom-pv-att-select',
      onPersist: (patch) => this.persistFieldChange(patch),
    });
    return (
      <>
        {shared || (
          <select
            className="bom-pv-att-select"
            value={attachmentIntToLabel(attachmentTypeInt, productId)}
            onChange={(e: any) => {
              const next = attachmentLabelToInt(e.target.value, productId);
              if (next !== undefined && dispatch) {
                dispatch(SET_PROJECT_OPTION('attachment_type', next));
                this.persistFieldChange({ attachment_type: next });
              }
            }}>
            {ATTACHMENT_TYPE_GROUPS.map(g => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </optgroup>
            ))}
          </select>
        )}
      </>
    );
  };

  // Delegates to the shared renderConditionalAttachmentFields helper
  // (also used by BomPanel). The onFieldChange callback maps every field
  // edit to CompleteBomPage's persistence flow: SET_PROJECT_OPTION for
  // Redux + persistFieldChange for the configOverride PUT that triggers
  // regen + BOM refresh — the same fields BomPanel writes, so navigating
  // between the two pages always shows consistent selections.
  private renderConditionalAttachmentFields = () => {
    const p = this.props;
    return renderSharedConditionalAttachmentFields({
      roofType: p.roofType,
      productId: this.resolveProductId(),
      attachmentTypeInt: p.attachmentTypeInt,
      s5OrSci: p.s5OrSci,
      tileReplacementOrSolarhooks: p.tileReplacementOrSolarhooks,
      railClampOption: p.railClampOption,
      materialThickness: p.materialThickness,
      roofSubstrate: p.roofSubstrate,
      standoffHeight: p.standoffHeight,
      manufacturer: p.manufacturer,
      colour: p.colour,
      isUbuilderLite: true,
      allowMechanicalAttachments: p.allowMechanicalAttachments,
      attachmentOptimizationCriteria: p.attachmentOptimizationCriteria,
      upliftAllowable: p.upliftAllowable,
      shearAllowable: p.shearAllowable,
      onFieldChange: (apiField, value) => {
        if (!p.dispatch) return;
        p.dispatch(SET_PROJECT_OPTION(apiField, value));
        this.persistFieldChange({ [apiField]: value });
      },
    });
  };

  private renderAccessoryCatalog = (catalogItems: BomPart[]) => {
    if (catalogItems.length === 0) return null;
    return (
      <div className="bom-pv-acc-catalog">
        <div className="bom-pv-acc-catalog-title">Available Accessories</div>
        <div className="bom-pv-acc-catalog-grid">
          {catalogItems.map(item => (
            <button
              key={item.number}
              type="button"
              className="bom-pv-acc-catalog-item"
              onClick={() => this.toggleAccessory(item.number)}>
              <span className="bom-pv-acc-catalog-lbl">{item.description || item.name}</span>
              <span className="bom-pv-acc-catalog-price">{fmtMoney(Number(item.price))}</span>
              <span className="bom-pv-acc-catalog-add">+</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  render() {
    const { projectName, partsList: propPartsList, accessoriesList, totalModules, totalWatts, domesticContent,
      bomReady, bomError } = this.props;

    // First-load gate. state.bom carries over from the workspace session in
    // the typical navigation, so bomReady is usually already true on mount
    // and this branch is skipped. The branch matters for: (a) the
    // CompleteBomPage opened via deep-link / refresh, (b) the brief window
    // before componentDidMount's FETCH_LITE_BOM_REQUEST returns.
    // Mirrors BomPanel: only gate on !bomReady. In-flight refresh cycles
    // (bomRefreshing) are surfaced via the bom-pv-update-overlay chip below
    // so the user sees the prior BOM faintly underneath, not a full blank
    // loading screen on every domestic_content / attachment regen.
    const projectId = this.props.match && this.props.match.params && this.props.match.params.projectId;
    if (projectId && !bomReady && !bomError) {
      return this.renderBomLoadingScreen();
    }

    const { moduleLength, snowLoad } = this.props;
    // The URL slug is authoritative for the product on this workspace-less
    // route — pc.productId (this.props.productId) stays at the store default
    // (solarmount_2 = 9), which makes isRM10Evolution/isRMGridFlex false and
    // silently drops the mandatory Mid Support injection. Resolve from the slug.
    const resolvedProductId = this.resolveProductId();
    const hideDomesticContentSwitch = isSolarMount(resolvedProductId || 0);
    const partsList = getAugmentedPartsList(propPartsList, resolvedProductId, domesticContent, totalModules, moduleLength, snowLoad);
    const all = (partsList || []).filter(p => !!p);
    // The backend's GET response can include an added accessory in
    // BOTH partsList (as a committed part with its catalog row) AND
    // accessoriesList (as a catalog entry). Filter accessory-numbered
    // items out of the parts-side sections so they render only once —
    // via the Accessories section — and so partSubtotal doesn't
    // double-count them against accSubtotal.
    const accessoryNumberSet = new Set((accessoriesList || []).map(a => a && a.number).filter(Boolean));
    const partsByNumber = new Map(partsList.map(p => [p.number, p]));
    const sortedParts = sortLiteBom(resolvedProductId, partsList);
    const partsForSections = sortedParts.filter(p => !accessoryNumberSet.has(p.number));
    const attachmentParts = partsForSections.filter(p => ATTACHMENT_TYPES.has(p.type));
    const baseParts = partsForSections.filter(p => !ATTACHMENT_TYPES.has(p.type));

    const accAll = (accessoriesList || []).filter(a => !!a);
    const addedAccessories = accAll.filter(a => isAccessoryAdded(a, this.state));
    const catalogAccessories = accAll.filter(a => !isAccessoryAdded(a, this.state));

    const sumOf = (parts: BomPart[], kind: 'part' | 'accessory') =>
      parts.reduce((s, p) => s + this.resolveRow(p, kind).total, 0);
    const attachmentSubtotal = sumOf(attachmentParts, 'part');
    const baseSubtotal = sumOf(baseParts, 'part');
    const accSubtotal = sumOf(addedAccessories, 'accessory');
    const partSubtotal = attachmentSubtotal + baseSubtotal;
    const subtotal = partSubtotal + accSubtotal;
    const discountPctNum = Math.max(0, Math.min(100, parseInt(this.state.discountPct, 10) || 0));
    const discountAmt = subtotal * (discountPctNum / 100);
    const grand = subtotal - discountAmt;

    const kw = totalWatts > 0 ? (totalWatts / 1000).toFixed(2) : '0.00';
    const cpw = totalWatts > 0 ? ((grand / totalWatts) * 100).toFixed(1) + '¢' : '—';

    return (
      <div className="bom-pv-overlay">
        <div className="bom-pv-nav">
          <div className="bom-pv-nav-left">
            <span className="v3-nav-logo-text" style={{ cursor: 'pointer' }} onClick={this.goDashboard} title="Go to Dashboard">
              <img src={require('assets/media/logos/U-Builder_Go_Logo.png')} style={{ height: 24 }} />
            </span>
            <div style={{ width: 1, height: 20, background: 'rgba(10,60,93,0.13)' }} />
            <div>
              <div className="bom-pv-nav-meta">
                <span>{fmtToday()}</span>
              </div>
            </div>
          </div>
          <div className="bom-pv-nav-actions">
            <button type="button" className="bom-pv-close-btn" onClick={this.goDashboard}>✕ Close</button>
          </div>
        </div>

        <div className="bom-pv-strip">
          <button type="button" className="bom-pv-back-btn" onClick={this.goBack}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M8 2L3 6.5 8 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Go Back
          </button>
          {!hideDomesticContentSwitch && (
            <div className="v3-dc-switch v3-dc-switch--compact bom-pv-dc-switch" onClick={this.handleDomesticToggle}>
              <div className="v3-dc-switch-info">
                <span className="v3-dc-switch-lbl">Domestic Content</span>
                {/* <span className="v3-dc-switch-sub">US installations only.</span> */}
              </div>
              <button type="button" className={`v3-dc-pill ${domesticContent ? 'active' : ''}`}
                role="switch" aria-checked={domesticContent}
                onClick={(e) => { e.stopPropagation(); this.handleDomesticToggle(); }}>
                <span className="v3-dc-pill-thumb" />
              </button>
              <img
                src={require('assets/media/logos/Domestic_content_flag.png')}
                alt="U-Builder"
                style={{ height: 32, width: 'auto', display: 'block' }}
              />
            </div>
          )}
          <div className="bom-pv-strip-spacer" />
          <div className="bom-pv-strip-actions">
            <button type="button" className="bom-pv-strip-icon-btn" onClick={() => window.print()} title="Print">
              <svg width="14" height="14" viewBox="0 0 13 13" fill="none">
                <path d="M3 4V1h7v3M3 9H1V5h11v4h-2M3 7h7v5H3V7z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <div className="bom-pv-dl-wrap">
              <button
                type="button"
                className={`bom-pv-strip-icon-btn ${this.state.downloadMenuOpen ? 'active' : ''}`}
                title="Download BOM"
                aria-haspopup="menu"
                aria-expanded={this.state.downloadMenuOpen}
                onClick={this.toggleDownloadMenu}>
                <svg width="14" height="14" viewBox="0 0 13 13" fill="none">
                  <path d="M2 9v2h9V9M6.5 2v7M4 6l2.5 3L9 6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {this.state.downloadMenuOpen && (
                <React.Fragment>
                  <div className="bom-pv-dl-backdrop" onClick={this.closeDownloadMenu} />
                  <div className="bom-pv-dl-menu" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      className="bom-pv-dl-menu-item"
                      onClick={this.handleDownloadBom}>
                      <span className="bom-pv-dl-menu-icon" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M3 1.5h6l3 3v10A0.5.5 0 0 1 11.5 15h-8a0.5.5 0 0 1-0.5-0.5v-12A0.5.5 0 0 1 3.5 2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                          <path d="M9 1.5v3h3" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                          <text x="4.7" y="12" fontSize="3.6" fontWeight="700" fill="currentColor" fontFamily="PT Sans, sans-serif">PDF</text>
                        </svg>
                      </span>
                      <span className="bom-pv-dl-menu-title">Download BOM PDF</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="bom-pv-dl-menu-item"
                      onClick={this.handleDownloadCsv}>
                      <span className="bom-pv-dl-menu-icon" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M3 1.5h6l3 3v10A0.5.5 0 0 1 11.5 15h-8a0.5.5 0 0 1-0.5-0.5v-12A0.5.5 0 0 1 3.5 2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                          <path d="M9 1.5v3h3" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
                          <text x="4.7" y="12" fontSize="3.6" fontWeight="700" fill="currentColor" fontFamily="PT Sans, sans-serif">CSV</text>
                        </svg>
                      </span>
                      <span className="bom-pv-dl-menu-title">Download CSV</span>
                    </button>
                  </div>
                </React.Fragment>
              )}
            </div>
            <button type="button" className="bom-pv-strip-dl-btn" onClick={this.openEmailModal}>
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <rect x="1" y="2.5" width="11" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.3" />
                <path d="M1 4l5.5 4L12 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Send Email
            </button>
          </div>
        </div>

        <div className="bom-pv-split bom-pv-split--with-update">
          {this.props.isUpdating && (
            <div className="bom-pv-update-overlay" aria-live="polite">
              <div className="v3-rp-bom-update-chip">
                <div className="v3-rp-bom-update-spinner" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 50 50" fill="none">
                    <circle cx="25" cy="25" r="20" stroke="rgba(0,59,92,.15)" strokeWidth="5" />
                    <path d="M25 5 a20 20 0 0 1 20 20" stroke="#003B5C" strokeWidth="5" strokeLinecap="round" />
                  </svg>
                </div>
                <span className="v3-rp-bom-update-msg">Updating BOM…</span>
              </div>
            </div>
          )}
          <div className="bom-pv-right">
            <div className="bom-pv-overview-sticky">
              <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span className="bom-pv-proj-overview">{projectName || 'Project'} : {projectId}</span>
                <div className="v3-rp-discount-left" style={{ fontSize: '12px' }}>
                  <span className="v3-rp-discount-lbl" style={{ marginRight: '4px' }}>Discount</span>
                  <input type="text" inputMode="numeric" pattern="[0-9]*"
                    className="v3-rp-discount-input"
                    style={{ width: '30px', textAlign: 'center', padding: '2px' }}
                    value={this.state.discountPct || ''} placeholder="0"
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw !== '' && !/^\d+$/.test(raw)) return;
                      if (raw !== '' && parseInt(raw, 10) > 100) return;
                      this.setState({ discountPct: raw }, this.scheduleBomSave);
                    }} />
                  <span className="v3-rp-discount-lbl" style={{ marginLeft: '2px' }}>%</span>
                </div>
              </div>
              <div className="bom-pv-mini-cards">
                <div className="bom-pv-mini-card">
                  <div className="bom-pv-mini-lbl">Panels</div>
                  <div className="bom-pv-mini-val">{totalModules || '—'}</div>
                </div>
                <div className="bom-pv-mini-card">
                  <div className="bom-pv-mini-lbl">System Size</div>
                  <div className="bom-pv-mini-val">{kw} kW</div>
                </div>
                <div className="bom-pv-mini-card">
                  <div className="bom-pv-mini-lbl">¢/Watt</div>
                  <div className="bom-pv-mini-val">{cpw}</div>
                </div>
                <div className="bom-pv-mini-card">
                  <div className="bom-pv-mini-lbl">Main Components</div>
                  <div className="bom-pv-mini-val">{fmtMoney(partSubtotal)}</div>
                </div>
                <div className="bom-pv-mini-card">
                  <div className="bom-pv-mini-lbl">Accessories</div>
                  <div className="bom-pv-mini-val">{fmtMoney(accSubtotal)}</div>
                </div>
                <div className="bom-pv-mini-card">
                  <div className="bom-pv-mini-lbl">Discount ({discountPctNum}%)</div>
                  <div className="bom-pv-mini-val" style={{ color: 'green' }}>−{fmtMoney(discountAmt)}</div>
                </div>
                <div className="bom-pv-mini-card bom-pv-mini-card--total">
                  <div className="bom-pv-mini-lbl">Total Price</div>
                  <div className="bom-pv-mini-val" style={{ color: 'orange' }}>{fmtMoney(grand)}</div>
                </div>
              </div>
            </div>

            <div className="bom-pv-section">
              <div className="bom-pv-section-hdr">
                <div className="bom-pv-section-icon bom-pv-section-icon--orange">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <path d="M7.5 1.5v8M4.5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M2.5 12.5h10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                </div>
                <span className="bom-pv-section-title">Attachment</span>
                <span className="bom-pv-section-count">
                  {attachmentParts.length} {attachmentParts.length === 1 ? 'part' : 'parts'}
                </span>
                <div className="bom-pv-section-hdr-spacer" style={{ flex: 1 }} />
                {!this.isCommercialRM() && this.renderAttachmentDropdown()}
              </div>
              {!this.isCommercialRM() && this.renderConditionalAttachmentFields()}
              <table className="bom-pv-table">
                <thead><tr>
                  <th>Part Name</th><th>Type</th>
                  <th className="num">Suggested</th><th className="num">Qty</th>
                  <th className="num">Unit Price</th><th className="num">Total Price</th>
                </tr></thead>
                <tbody>
                  {attachmentParts.length === 0 ? (
                    <tr><td colSpan={6} className="bom-pv-empty-acc">No attachments selected</td></tr>
                  ) : attachmentParts.map(p => this.renderEditableRow(p, 'att', 'Attachment', 'part', undefined, partsByNumber))}
                </tbody>
              </table>
              <div className="bom-pv-section-foot">
                <span className="bom-pv-section-foot-lbl">Attachment subtotal</span>
                <span className="bom-pv-section-foot-val">{fmtMoney(attachmentSubtotal)}</span>
              </div>
            </div>

            <div className="bom-pv-section">
              <div className="bom-pv-section-hdr">
                <div className="bom-pv-section-icon bom-pv-section-icon--navy">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <rect x="1" y="1" width="13" height="13" rx="2" stroke="currentColor" strokeWidth="1.3" />
                    <path d="M4 5h7M4 8h7M4 11h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </div>
                <span className="bom-pv-section-title">Main Components</span>
                <span className="bom-pv-section-count">{baseParts.length} {baseParts.length === 1 ? 'part' : 'parts'}</span>
              </div>
              <table className="bom-pv-table">
                <thead><tr>
                  <th>Part Name</th><th>Type</th>
                  <th className="num">Suggested</th><th className="num">Qty</th>
                  <th className="num">Unit Price</th><th className="num">Total Price</th>
                </tr></thead>
                <tbody>
                  {baseParts.length === 0 ? (
                    <tr><td colSpan={6} className="bom-pv-empty-acc">Drop panels on the canvas to generate a BOM.</td></tr>
                  ) : baseParts.map(p => this.renderEditableRow(p, 'sys', 'System', 'part', undefined, partsByNumber))}
                </tbody>
              </table>
              <div className="bom-pv-section-foot">
                <span className="bom-pv-section-foot-lbl">Main Components subtotal</span>
                <span className="bom-pv-section-foot-val">{fmtMoney(baseSubtotal)}</span>
              </div>
            </div>

            <div className="bom-pv-section">
              <div className="bom-pv-section-hdr">
                <div className="bom-pv-section-icon bom-pv-section-icon--orange">
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                    <circle cx="7.5" cy="7.5" r="6" stroke="currentColor" strokeWidth="1.3" />
                    <path d="M7.5 4v4l2.5 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                </div>
                <span className="bom-pv-section-title">Accessories</span>
                <span className="bom-pv-section-count">{addedAccessories.length} {addedAccessories.length === 1 ? 'item' : 'items'}</span>
              </div>
              <table className="bom-pv-table">
                <thead><tr>
                  <th>Part Name</th><th>Type</th>
                  <th className="num">Suggested</th><th className="num">Qty</th>
                  <th className="num">Unit Price</th><th className="num">Total Price</th>
                </tr></thead>
                <tbody>
                  {addedAccessories.length === 0 ? (
                    <tr><td colSpan={6} className="bom-pv-empty-acc">No accessories added</td></tr>
                  ) : addedAccessories.map(a =>
                    this.renderEditableRow(a, 'acc', 'Accessory', 'accessory',
                      () => this.toggleAccessory(a.number), partsByNumber))}
                </tbody>
              </table>
              {this.renderAccessoryCatalog(catalogAccessories)}
              <div className="bom-pv-section-foot">
                <span className="bom-pv-section-foot-lbl">Accessories subtotal</span>
                <span className="bom-pv-section-foot-val">{fmtMoney(accSubtotal)}</span>
              </div>
            </div>

          </div>
        </div>

        {this.renderEmailModal()}
        {this.renderReportDownloadOverlay()}
        {this.state.toastMsg && (
          <div className="bom-toast show">{this.state.toastMsg}</div>
        )}
      </div>
    );
  }

  renderBomLoadingScreen() {
    // Full-page loader for the initial fetch. Reuses the .v3-rp-bom-loading*
    // classes from v3styles.scss (defined for BomPanel) inside a page-level
    // container so it visually matches the workspace's right-panel loader.
    // Includes the nav strip so the user retains a Close affordance without
    // having to wait for the BOM to load.
    return (
      <div className="bom-pv-overlay">
        <div className="bom-pv-nav">
          <div className="bom-pv-nav-left">
            <span className="v3-nav-logo-text" style={{ cursor: 'pointer' }} onClick={this.goDashboard} title="Go to Dashboard">
              <img src={require('assets/media/logos/U-Builder_Go_Logo.png')} style={{ height: 24 }} />
            </span>
          </div>
          <div className="bom-pv-nav-actions">
            <button type="button" className="bom-pv-close-btn" onClick={this.goDashboard}>✕ Close</button>
          </div>
        </div>
        <div className="bom-pv-loading-host">
          <div className="v3-rp-bom-loading">
            <div className="v3-rp-bom-loading-spinner" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 50 50" fill="none">
                <circle cx="25" cy="25" r="20" stroke="rgba(0,59,92,.15)" strokeWidth="4" />
                <path d="M25 5 a20 20 0 0 1 20 20" stroke="#003B5C" strokeWidth="4" strokeLinecap="round" />
              </svg>
            </div>
            <div className="v3-rp-bom-loading-msg">Loading BOM…</div>
            <div className="v3-rp-bom-loading-sub">Fetching parts list from the server.</div>
          </div>
        </div>
      </div>
    );
  }

  renderReportDownloadOverlay() {
    if (!this.state.reportDownloading) return null;
    const msg = this.state.reportLoaderMsg || 'Preparing report…';
    return (
      <div className="bom-pv-download-toast" aria-live="polite">
        <div className="bom-pv-download-toast-spinner">
          <svg width="18" height="18" viewBox="0 0 50 50" fill="none">
            <circle cx="25" cy="25" r="20" stroke="rgba(255,255,255,.3)" strokeWidth="5" />
            <path d="M25 5 a20 20 0 0 1 20 20" stroke="#fff" strokeWidth="5" strokeLinecap="round" />
          </svg>
        </div>
        <span className="bom-pv-download-toast-msg">Generating PDF… {msg}</span>
      </div>
    );
  }

  renderEmailModal() {
    const { emailModalOpen, emailModalView, emailRecipient, emailMessage, emailSending } = this.state;
    if (!emailModalOpen) return null;
    const sendEnabled = EMAIL_RE.test(emailRecipient.trim()) && !emailSending;
    const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) this.closeEmailModal();
    };

    return (
      <div className="email-modal-overlay show" onClick={onBackdropClick}>
        <div className="email-modal-card">
          {emailModalView === 'default' ? (
            <>
              <div className="email-modal-hdr">
                <div>
                  <div className="email-modal-title">Send Export Package</div>
                  <div className="email-modal-sub">We'll email everything selected in this export.</div>
                </div>
                <button type="button" className="email-modal-close" onClick={this.closeEmailModal} aria-label="Close">
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
              <div className="email-modal-body">
                <div className="email-modal-what">
                  <div className="email-modal-what-row">
                    <div className="email-modal-what-dot" style={{ background: '#003B5C' }} />
                    <span>Bill of Materials</span>
                  </div>
                </div>
                <div className="email-modal-field">
                  <label htmlFor="bom-email-recipient">Recipient Email</label>
                  <input
                    id="bom-email-recipient"
                    className="email-modal-input"
                    type="email"
                    placeholder="e.g. client@example.com"
                    value={emailRecipient}
                    onChange={(e) => this.setState({ emailRecipient: e.target.value })}
                  />
                </div>
                <div className="email-modal-field" style={{ marginTop: 12 }}>
                  <label htmlFor="bom-email-message">
                    Message <span style={{ fontWeight: 400, color: '#5A7D95' }}>(optional)</span>
                  </label>
                  <textarea
                    id="bom-email-message"
                    className="email-modal-input"
                    rows={3}
                    placeholder="Add a note to the recipient…"
                    style={{ resize: 'vertical', lineHeight: 1.5 }}
                    value={emailMessage}
                    onChange={(e) => this.setState({ emailMessage: e.target.value })}
                  />
                </div>
              </div>
              <div className="email-modal-ftr">
                <button type="button" className="email-modal-btn email-modal-btn--ghost" onClick={this.closeEmailModal}>Cancel</button>
                <button type="button"
                  className="email-modal-btn email-modal-btn--primary"
                  onClick={this.sendExportEmail}
                  disabled={!sendEnabled}>
                  {emailSending ? 'Sending…' : (
                    <>
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path d="M1 6.5L12 1l-2.5 11-3.5-4L12 1M6 8.5L4.5 12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Send Package
                    </>
                  )}
                </button>
              </div>
            </>
          ) : (
            <div className="email-success show">
              <div className="email-success-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M4 12l5 5L20 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="email-success-title">Email Sent!</div>
              <div className="email-success-sub">Export package sent to {emailRecipient.trim()}</div>
              <button type="button" className="email-modal-btn email-modal-btn--ghost" style={{ marginTop: 4 }} onClick={this.closeEmailModal}>Done</button>
            </div>
          )}
        </div>
      </div>
    );
  }
}

function mapStateToProps(appState: any) {
  const bom = appState.bom || {};
  const pc = appState.projectConfiguration || {};
  const epd = appState.engineeringProjectDocuments || {};
  const env = (pc && pc.projectEnvConfig) || {};
  const app = appState.app || {};
  const liteProject = appState.liteProject || {};
  const ms = appState.moduleSelector || {};
  // On this workspace-less route projectEnvConfig is empty on first load. Read
  // each env field from Redux when present, else from the persisted snapshot the
  // BOM GET carries in extra_parameters.lite_project_env (see backend
  // serialized_data). A local edit dispatches SET_PROJECT_OPTION, repopulating
  // env so it takes precedence thereafter.
  const liteEnv = (bom.extraParameters && bom.extraParameters.lite_project_env) || {};
  const envOr = (key: string) =>
    (env[key] !== undefined && env[key] !== null) ? env[key] : liteEnv[key];
  // Identical to BomPanel.mapStateToProps. liteDirty covers the pre-autosave
  // window, liteProject.updating covers the PUT, liteProject.bomRefreshing
  // bridges PUT-success → GET-BOM, bom.isLoading is the legacy fetch flag.
  const isUpdating = !!app.liteDirty || !!liteProject.updating || !!liteProject.bomRefreshing || !!bom.isLoading;
  return {
    projectName: pc.projectName || '',
    partsList: bom.partsList || [],
    accessoriesList: bom.accessoriesList || [],
    totalModules: bom.total_modules || 0,
    totalWatts: bom.total_watts || 0,
    unitsType: epd.units || 'customary',
    // This route never mounts the workspace, so projectEnvConfig is empty on
    // first load and env.domestic_content is undefined — the toggle would
    // always render OFF even when the project was saved with it ON. Fall back
    // to the value the BOM GET carries in extra_parameters (the server's
    // persisted flag). Once the user toggles here, SET_PROJECT_OPTION populates
    // env.domestic_content, which then takes precedence for instant feedback.
    domesticContent: (env.domestic_content !== undefined && env.domestic_content !== null)
      ? !!env.domestic_content
      : !!(bom.extraParameters && bom.extraParameters.domestic_content),
    isUpdating,
    bomReady: !!bom.isReady,
    bomError: bom.error || null,
    extraParameters: bom.extraParameters || {},
    productId: pc.productId,
    // Same workspace-less-route problem as domestic_content above: env is empty
    // on first load, so the attachment-type dropdown and its dependent field
    // rows would render unselected even when the project was saved with them.
    // Fall back to the persisted values the BOM GET carries in
    // extra_parameters.lite_project_env. Once the user edits one here,
    // SET_PROJECT_OPTION populates env, which takes precedence for instant
    // feedback.
    attachmentTypeInt: envOr('attachment_type'),
    roofType: envOr('roof_type'),
    s5OrSci: envOr('s5_or_sci'),
    tileReplacementOrSolarhooks: envOr('tile_replacement_or_solarhooks'),
    railsProductId: pc.railsProductId,
    railClampOption: envOr('rail_clamp_option'),
    materialThickness: envOr('material_thickness'),
    s5RoofManufacturer: envOr('s5_roof_manufacturer'),
    s5RoofPanel: envOr('s5_roof_panel'),
    s5RoofMaterial: envOr('s5_roof_material'),
    s5Clamp: envOr('s5_clamp'),
    s5UltimateLoadUplift: envOr('s5_ultimate_load_uplift'),
    s5UltimateLoadShear: envOr('s5_ultimate_load_shear'),
    roofSubstrate: envOr('roof_substrate'),
    standoffHeight: envOr('standoff_height'),
    manufacturer: env.manufacturer,
    colour: env.colour,
    allowMechanicalAttachments: envOr('allow_mechanical_attachments'),
    attachmentOptimizationCriteria: envOr('attachment_optimization_criteria'),
    upliftAllowable: envOr('uplift_allowable'),
    shearAllowable: envOr('shear_allowable'),
    railFinish: envOr('rail_finish'),
    moduleLength: Number((ms.modelData && ms.modelData.height) || envOr('module_length')) || 0,
    snowLoad: Number(envOr('snow_load')) || 0,
    uanchorPartNumber: envOr('uanchor_part_number'),
  };
}

export default connect(mapStateToProps)(CompleteBomPage);
